import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';

import { Button, Card } from '@/design-system';
import type { SubscriptionOverview } from '@/entities/credit';
import { formatCurrencyFromCents } from '@/shared/lib';

import { billingCopy } from '../copy';
import { withVat } from '../vat';

/** Het anker waar "Extra keuringen kopen" en "Losse rapportage kopen" naartoe wijzen (`paths.buyInspections`). */
export const BUY_INSPECTIONS_ANCHOR = 'keuringen-kopen';

export interface PurchaseCardProps {
    purchase: SubscriptionOverview['purchase'];
    /** Met een abonnement is een keuring een extra keuring; zonder een losse rapportage. */
    hasSubscription: boolean;
    busy: boolean;
    error: string | null;
    onBuy: (quantity: number) => void;
}

/**
 * Keuringen kopen met een eenmalige betaling.
 *
 * Wat een keuring is en wat hij kost, zegt de API (`purchase` van
 * `GET /billing/subscription`): met een abonnement een extra keuring, zonder een
 * losse rapportage. Het scherm kiest alleen het aantal (1 tot en met 20) en
 * rekent de btw net zo uit als de API, zodat het getoonde totaal het bedrag is
 * dat wordt afgeschreven.
 *
 * "Extra keuringen kopen" op het dashboard en bij een 402 komt hier uit
 * (`#keuringen-kopen`), en het blok scrolt zich dan in beeld.
 */
export function PurchaseCard({ purchase, hasSubscription, busy, error, onBuy }: PurchaseCardProps) {
    const { hash } = useLocation();
    const ref = useRef<HTMLDivElement>(null);
    const [quantity, setQuantity] = useState(purchase.minQuantity);

    useEffect(() => {
        if (hash === `#${BUY_INSPECTIONS_ANCHOR}`) {
            ref.current?.scrollIntoView?.({ block: 'start' });
        }
    }, [hash]);
    const split = withVat(purchase.unitPriceCents * quantity, purchase.vatPercent);
    const money = (cents: number) => formatCurrencyFromCents(cents, { cents: true });

    return (
        <Card
            ref={ref}
            id={BUY_INSPECTIONS_ANCHOR}
            padding="md"
            as="section"
            aria-labelledby="purchase-title"
        >
            <h2
                id="purchase-title"
                className="m-0 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {billingCopy.buyTitle}
            </h2>
            <p className="m-0 mt-2 text-[13px] leading-[1.5] text-[var(--text-secondary)]">
                {billingCopy.buyIntro(purchase.name, hasSubscription)}
            </p>
            <p className="m-0 mt-2 text-[13px] leading-[1.5] text-[var(--text-primary)]">
                {billingCopy.buyUnitPrice(purchase.name, money(purchase.unitPriceCents))}
            </p>

            <div
                className="mt-3 flex items-center gap-2"
                role="group"
                aria-label={billingCopy.buyQuantity}
            >
                <Button
                    variant="outline"
                    size="md"
                    aria-label={billingCopy.buyLess}
                    disabled={quantity <= purchase.minQuantity || busy}
                    onClick={() =>
                        setQuantity((current) => Math.max(purchase.minQuantity, current - 1))
                    }
                >
                    −
                </Button>
                <span className="inline-flex min-h-[44px] min-w-[56px] items-center justify-center rounded-[7px] border border-[var(--border-default)] px-3 font-[family-name:var(--font-mono)] text-[15px] text-[var(--text-primary)]">
                    {quantity}
                </span>
                <Button
                    variant="outline"
                    size="md"
                    aria-label={billingCopy.buyMore}
                    disabled={quantity >= purchase.maxQuantity || busy}
                    onClick={() =>
                        setQuantity((current) => Math.min(purchase.maxQuantity, current + 1))
                    }
                >
                    +
                </Button>
            </div>

            <dl className="m-0 mt-3 border-t border-[var(--border-subtle-2)] pt-2">
                <Row label={billingCopy.buyExclVat}>{money(split.netCents)}</Row>
                <Row label={billingCopy.buyVat(purchase.vatPercent)}>{money(split.vatCents)}</Row>
                <Row label={billingCopy.buyTotal} strong>
                    {money(split.totalCents)}
                </Row>
            </dl>

            <p className="m-0 mt-2 text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                {billingCopy.buyHelp}
            </p>

            <Button
                variant="primary"
                size="md"
                fullWidth
                className="mt-3"
                disabled={busy}
                onClick={() => onBuy(quantity)}
            >
                {billingCopy.buySubmit}
            </Button>

            {error ? (
                <p role="alert" className="m-0 mt-2 text-[12px] leading-[1.4] text-[var(--danger)]">
                    {error}
                </p>
            ) : null}
        </Card>
    );
}

function Row({
    label,
    children,
    strong = false,
}: {
    label: string;
    children: ReactNode;
    strong?: boolean;
}) {
    return (
        <div className="flex items-center justify-between gap-3 py-[5px]">
            <dt className="text-[13px] leading-[1.4] text-[var(--text-secondary)]">{label}</dt>
            <dd
                className={`m-0 text-right font-[family-name:var(--font-mono)] text-[12px] text-[var(--text-primary)] ${strong ? 'font-semibold' : ''}`}
            >
                {children}
            </dd>
        </div>
    );
}
